/**
 * React hook for i18n integration.
 *
 * Initializes the translation dictionary on mount and checks for updates
 * on route changes. Uses the active org ID for tenant-specific overrides.
 *
 * Subscribes to dictionary changes so the component tree re-renders when
 * translations are loaded asynchronously (e.g. after a fresh page load).
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  loadDictionary,
  checkVersion,
  detectLocale,
  setLocale,
  getLocale,
  isDictionaryLoaded,
  onDictionaryChange,
} from '@/lib/i18n';
import { storageKey } from '@/config/app';

/**
 * The super-admin area is a platform-level tool that must always render in
 * English and must never pick up tenant-specific translation overrides.
 * Otherwise an org admin who has set German (or customised keys like
 * "Organization" → "Organisation") will see those strings leak into
 * /admin pages — which looked buggy in the OrgsList table (bug fix v1.0.11).
 */
function isAdminRoute(pathname: string): boolean {
  return pathname === '/admin' || pathname.startsWith('/admin/');
}

export function useI18n() {
  const location = useLocation();
  const adminRoute = isAdminRoute(location.pathname);
  const orgId = adminRoute
    ? undefined
    : localStorage.getItem(storageKey('active_org_id')) || undefined;
  const prevPath = useRef(location.pathname);

  // Force re-render when the dictionary changes (e.g. async load completes)
  const [, setDictVersion] = useState(0);
  useEffect(() => {
    return onDictionaryChange(() => setDictVersion((v) => v + 1));
  }, []);

  // Initialize on mount.
  // Non-admin locale is owned by the route loaders (org default + override).
  // We only force English here for admin routes, and fall back to loading the
  // dictionary if the loader didn't (e.g. direct mount in tests).
  useEffect(() => {
    if (adminRoute) {
      if (getLocale() !== 'en') {
        setLocale('en');
      } else if (!isDictionaryLoaded()) {
        loadDictionary(undefined);
      }
    } else if (!isDictionaryLoaded()) {
      loadDictionary(orgId);
    }
  }, []);

  // Reload dictionary whenever we cross the admin / non-admin boundary, or
  // on any route change within the same side (cheap version check).
  useEffect(() => {
    if (prevPath.current === location.pathname) return;

    const wasAdmin = isAdminRoute(prevPath.current);
    prevPath.current = location.pathname;

    if (wasAdmin !== adminRoute) {
      // Boundary crossed — swap locale and reload the dictionary from scratch.
      // Non-admin uses detectLocale (override → browser → en); the loader
      // already set the org default so this should usually be a no-op.
      const targetLocale = adminRoute ? 'en' : detectLocale();
      if (targetLocale !== getLocale()) {
        setLocale(targetLocale);
      } else {
        loadDictionary(orgId);
      }
    } else {
      checkVersion(orgId);
    }
  }, [location.pathname, orgId, adminRoute]);
}
